import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
  subYears,
} from 'date-fns';

export const INVOICE_FILTER_STATUSES = [
  'draft',
  'issued',
  'partial',
  'paid',
  'overdue',
  'cancelled',
  'refunded',
  'void',
] as const;

export type InvoiceFilterStatus = (typeof INVOICE_FILTER_STATUSES)[number];

export const INVOICE_DATE_FIELDS = ['issued_at', 'due_at', 'paid_at'] as const;
export type InvoiceDateField = (typeof INVOICE_DATE_FIELDS)[number];

export const INVOICE_DATE_PRESETS = [
  'today',
  'this_week',
  'this_month',
  'last_month',
  'this_year',
  'last_year',
  'custom',
] as const;
export type InvoiceDatePreset = (typeof INVOICE_DATE_PRESETS)[number];

export interface InvoicePageFilters {
  statuses: InvoiceFilterStatus[];
  patientIds: string[];
  dateField: InvoiceDateField;
  datePreset: InvoiceDatePreset | null;
  customFrom: string | null;
  customTo: string | null;
  amountMinCents: number | null;
  amountMaxCents: number | null;
  paymentMethods: string[];
  search: string;
}

export const EMPTY_INVOICE_FILTERS: InvoicePageFilters = {
  statuses: [],
  patientIds: [],
  dateField: 'issued_at',
  datePreset: null,
  customFrom: null,
  customTo: null,
  amountMinCents: null,
  amountMaxCents: null,
  paymentMethods: [],
  search: '',
};

export interface InvoiceFilterRow {
  status: string;
  patient_id: string;
  invoice_number: string | null;
  patient_first_name: string | null;
  patient_last_name: string | null;
  total_cents: number;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  last_payment_at: string | null;
  payment_method?: string | null;
}

const DATE_FIELD_TO_PARAM: Record<InvoiceDateField, string> = {
  issued_at: 'issuedDate',
  due_at: 'dueDate',
  paid_at: 'paymentDate',
};

const PARAM_TO_DATE_FIELD: Record<string, InvoiceDateField> = {
  issueddate: 'issued_at',
  issued_at: 'issued_at',
  issued: 'issued_at',
  duedate: 'due_at',
  due_at: 'due_at',
  due: 'due_at',
  paymentdate: 'paid_at',
  paid_at: 'paid_at',
  paid: 'paid_at',
};

function isFilterStatus(value: string): value is InvoiceFilterStatus {
  return (INVOICE_FILTER_STATUSES as readonly string[]).includes(value);
}

function isDatePreset(value: string): value is InvoiceDatePreset {
  return (INVOICE_DATE_PRESETS as readonly string[]).includes(value);
}

function splitCsv(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function parseFilterAmountToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/\s/g, '').replace(',', '.');
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

export function formatFilterAmountInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

export function parseInvoiceFiltersFromSearch(params: URLSearchParams): InvoicePageFilters {
  const statuses = splitCsv(params.get('status')).filter(isFilterStatus);
  const patientIds = splitCsv(params.get('patient'));
  const dateFieldKey = (params.get('dateField') ?? '').toLowerCase();
  const dateField = PARAM_TO_DATE_FIELD[dateFieldKey] ?? 'issued_at';
  const dateParam = params.get('date');
  const datePreset = dateParam && isDatePreset(dateParam) ? dateParam : null;
  const customFrom = params.get('from');
  const customTo = params.get('to');
  const min = params.get('min');
  const max = params.get('max');

  return {
    statuses,
    patientIds,
    dateField,
    datePreset: datePreset ?? (customFrom || customTo ? 'custom' : null),
    customFrom: customFrom || null,
    customTo: customTo || null,
    amountMinCents: min ? parseFilterAmountToCents(min) : null,
    amountMaxCents: max ? parseFilterAmountToCents(max) : null,
    paymentMethods: splitCsv(params.get('method')),
    search: params.get('q')?.trim() ?? '',
  };
}

export function invoiceFiltersToSearchParams(filters: InvoicePageFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.statuses.length > 0) params.set('status', filters.statuses.join(','));
  if (filters.patientIds.length > 0) params.set('patient', filters.patientIds.join(','));
  if (filters.datePreset) {
    params.set('dateField', DATE_FIELD_TO_PARAM[filters.dateField]);
    params.set('date', filters.datePreset);
    if (filters.datePreset === 'custom') {
      if (filters.customFrom) params.set('from', filters.customFrom);
      if (filters.customTo) params.set('to', filters.customTo);
    }
  }
  if (filters.amountMinCents != null) {
    params.set('min', (filters.amountMinCents / 100).toString());
  }
  if (filters.amountMaxCents != null) {
    params.set('max', (filters.amountMaxCents / 100).toString());
  }
  if (filters.paymentMethods.length > 0) params.set('method', filters.paymentMethods.join(','));
  if (filters.search.trim()) params.set('q', filters.search.trim());
  return params;
}

const WEEK_OPTIONS = { weekStartsOn: 1 as const };

export function resolveInvoiceDateRange(
  filters: Pick<InvoicePageFilters, 'datePreset' | 'customFrom' | 'customTo'>,
  now: Date,
): { start: Date; end: Date } | null {
  if (!filters.datePreset) return null;

  switch (filters.datePreset) {
    case 'today':
      return { start: startOfDay(now), end: endOfDay(now) };
    case 'this_week':
      return { start: startOfWeek(now, WEEK_OPTIONS), end: endOfWeek(now, WEEK_OPTIONS) };
    case 'this_month':
      return { start: startOfMonth(now), end: endOfMonth(now) };
    case 'last_month': {
      const previous = subMonths(now, 1);
      return { start: startOfMonth(previous), end: endOfMonth(previous) };
    }
    case 'this_year':
      return { start: startOfYear(now), end: endOfYear(now) };
    case 'last_year': {
      const previous = subYears(now, 1);
      return { start: startOfYear(previous), end: endOfYear(previous) };
    }
    case 'custom': {
      if (!filters.customFrom && !filters.customTo) return null;
      const start = filters.customFrom
        ? startOfDay(new Date(`${filters.customFrom}T00:00:00`))
        : startOfDay(new Date(0));
      const end = filters.customTo
        ? endOfDay(new Date(`${filters.customTo}T00:00:00`))
        : endOfDay(now);
      return { start, end };
    }
  }
}

export function invoicePaymentDate(row: InvoiceFilterRow): string | null {
  return row.paid_at ?? row.last_payment_at;
}

export function invoiceDateValue(row: InvoiceFilterRow, field: InvoiceDateField): string | null {
  if (field === 'paid_at') return invoicePaymentDate(row);
  return row[field];
}

export function isInvoiceOverdue(row: InvoiceFilterRow, now: Date): boolean {
  if (row.status === 'overdue') return true;
  return row.status === 'issued' && row.due_at != null && new Date(row.due_at) < now;
}

export function matchesInvoiceStatuses(
  row: InvoiceFilterRow,
  statuses: InvoiceFilterStatus[],
  now: Date,
): boolean {
  if (statuses.length === 0) return true;
  return statuses.some((status) => {
    if (status === 'overdue') return isInvoiceOverdue(row, now);
    return row.status === status;
  });
}

export function matchesInvoiceSearch(row: InvoiceFilterRow, search: string): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  const name = `${row.patient_first_name ?? ''} ${row.patient_last_name ?? ''}`
    .trim()
    .toLowerCase();
  return (
    (row.invoice_number ?? '').toLowerCase().includes(term) ||
    name.includes(term) ||
    (row.patient_first_name ?? '').toLowerCase().includes(term) ||
    (row.patient_last_name ?? '').toLowerCase().includes(term)
  );
}

export function matchesInvoiceFilters(
  row: InvoiceFilterRow,
  filters: InvoicePageFilters,
  now: Date,
  methodsByInvoiceId?: Map<string, string[]>,
  invoiceId?: string,
): boolean {
  if (!matchesInvoiceStatuses(row, filters.statuses, now)) return false;
  if (filters.patientIds.length > 0 && !filters.patientIds.includes(row.patient_id)) return false;
  if (!matchesInvoiceSearch(row, filters.search)) return false;

  const range = resolveInvoiceDateRange(filters, now);
  if (range) {
    const value = invoiceDateValue(row, filters.dateField);
    if (!value) return false;
    const time = new Date(value).getTime();
    if (time < range.start.getTime() || time > range.end.getTime()) return false;
  }

  if (filters.amountMinCents != null && row.total_cents < filters.amountMinCents) return false;
  if (filters.amountMaxCents != null && row.total_cents > filters.amountMaxCents) return false;

  if (filters.paymentMethods.length > 0) {
    const methods = new Set<string>();
    if (row.payment_method) methods.add(row.payment_method);
    if (invoiceId && methodsByInvoiceId) {
      for (const method of methodsByInvoiceId.get(invoiceId) ?? []) methods.add(method);
    }
    if (![...filters.paymentMethods].some((method) => methods.has(method))) return false;
  }

  return true;
}

export function countActiveInvoiceFilterGroups(
  filters: InvoicePageFilters,
  now = new Date(),
): number {
  let count = 0;
  if (filters.statuses.length > 0) count += 1;
  if (filters.patientIds.length > 0) count += 1;
  if (resolveInvoiceDateRange(filters, now)) count += 1;
  if (filters.amountMinCents != null || filters.amountMaxCents != null) count += 1;
  if (filters.paymentMethods.length > 0) count += 1;
  return count;
}

export function invoiceFiltersFromStatusTab(
  tab: InvoiceFilterStatus | 'all',
  current: InvoicePageFilters,
): InvoicePageFilters {
  return {
    ...current,
    statuses: tab === 'all' ? [] : [tab],
  };
}

export function activeInvoiceStatusTab(
  filters: InvoicePageFilters,
): InvoiceFilterStatus | 'all' | null {
  if (filters.statuses.length === 0) return 'all';
  if (filters.statuses.length === 1) return filters.statuses[0] ?? 'all';
  return null;
}

export function dbStatusesForQuery(
  statuses: InvoiceFilterStatus[],
): Array<Exclude<InvoiceFilterStatus, never>> | null {
  if (statuses.length === 0) return null;
  const next = new Set<InvoiceFilterStatus>(statuses);
  if (next.has('overdue')) {
    next.add('issued');
    next.add('overdue');
  }
  return [...next];
}

export type InvoiceReportId = 'paidThisMonth' | 'outstanding' | 'overdue' | 'partial';

export const INVOICE_REPORT_FILTERS: Record<InvoiceReportId, InvoicePageFilters> = {
  paidThisMonth: {
    ...EMPTY_INVOICE_FILTERS,
    statuses: ['paid'],
    dateField: 'paid_at',
    datePreset: 'this_month',
  },
  outstanding: {
    ...EMPTY_INVOICE_FILTERS,
    statuses: ['issued', 'partial', 'overdue'],
  },
  overdue: {
    ...EMPTY_INVOICE_FILTERS,
    statuses: ['overdue'],
  },
  partial: {
    ...EMPTY_INVOICE_FILTERS,
    statuses: ['partial'],
  },
};

export interface InvoiceListQueryFilters {
  statuses?: InvoiceFilterStatus[];
  search?: string;
  patientIds?: string[];
  dateField?: InvoiceDateField;
  dateFrom?: string;
  dateTo?: string;
  amountMinCents?: number;
  amountMaxCents?: number;
}

/** Server-side subset of a page filter. Payment date stays client-side. */
export function toInvoiceListQueryFilters(
  filters: InvoicePageFilters,
  now: Date,
): InvoiceListQueryFilters {
  const range = resolveInvoiceDateRange(filters, now);
  const applyServerDate = filters.dateField !== 'paid_at' && range != null;
  return {
    statuses: dbStatusesForQuery(filters.statuses) ?? undefined,
    search: filters.search || undefined,
    patientIds: filters.patientIds.length > 0 ? filters.patientIds : undefined,
    dateField: applyServerDate && range ? filters.dateField : undefined,
    dateFrom: applyServerDate && range ? range.start.toISOString() : undefined,
    dateTo: applyServerDate && range ? range.end.toISOString() : undefined,
    amountMinCents: filters.amountMinCents ?? undefined,
    amountMaxCents: filters.amountMaxCents ?? undefined,
  };
}

export function matchingInvoiceRows<T extends InvoiceFilterRow>(
  rows: readonly T[],
  filters: InvoicePageFilters,
  now: Date,
  methodsByInvoiceId?: Map<string, string[]>,
  idOf?: (row: T) => string | undefined,
): T[] {
  return rows.filter((row) =>
    matchesInvoiceFilters(row, filters, now, methodsByInvoiceId, idOf?.(row)),
  );
}

export function sumMatchingInvoiceTotals<T extends InvoiceFilterRow>(
  rows: readonly T[],
  filters: InvoicePageFilters,
  now: Date,
): number {
  return matchingInvoiceRows(rows, filters, now).reduce((sum, row) => sum + row.total_cents, 0);
}
